import { Component, OnInit, Output, EventEmitter, Input } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { ItineraryService } from '../../itinerary.service';
import { finalize, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

// Letras (con acentos), espacios, apóstrofo y guion
const NAME_PATTERN = /^[a-zA-ZÀ-ÿñÑ' -]*$/;
// Caracteres válidos de un email; permite búsquedas parciales (ej: "juan@")
const EMAIL_SEARCH_PATTERN = /^[a-zA-Z0-9._%+@-]*$/;

interface ClientFilters {
  firstName: string;
  lastName: string;
  email: string;
}

const EMPTY_FILTERS: ClientFilters = { firstName: '', lastName: '', email: '' };

@Component({
  selector: 'usb-client-list-modal',
  templateUrl: './client-list-modal.component.html',
  styleUrls: ['./client-list-modal.component.scss']
})
export class ClientListModalComponent implements OnInit {
  // Lista de clientes (pagina actual del servidor)
  clients: any[] = [];

  // Cliente seleccionado (para mantener el estado del checkbox)
  selectedClientEmail: string | null = null;

  // Input
  @Input() clientEmail: any = '';

  // Output para emitir cliente seleccionado
  @Output() clientSelected = new EventEmitter<any>();

  // Paginación
  currentPage: number = 1;
  itemsPerPage: number = 4;
  totalItems: number = 0;
  lastPage: number = 1;

  // Formulario de filtros
  filterForm: FormGroup;
  searchText: string = '';

  // Búsqueda en curso (deshabilita Buscar para evitar envíos duplicados)
  loading = false;

  // Datos de países para resolver phone_code
  countriesData: any[] = [];

  // Filtros aplicados en la última búsqueda (los usa la paginación, así
  // cambiar un campo sin presionar Buscar no altera las páginas siguientes)
  private appliedFilters: ClientFilters = { ...EMPTY_FILTERS };
  private _unsubscribeAll: Subject<any> = new Subject<any>();
  private agency: any;

  constructor(
    public bsModalRef: BsModalRef,
    private itineraryService: ItineraryService,
    private fb: FormBuilder
  ) {
    this.filterForm = this.fb.group({
      firstName: ['', [Validators.maxLength(50), Validators.pattern(NAME_PATTERN)]],
      lastName: ['', [Validators.maxLength(50), Validators.pattern(NAME_PATTERN)]],
      email: ['', [Validators.maxLength(100), Validators.pattern(EMAIL_SEARCH_PATTERN)]],
    });
  }

  ngOnInit(): void {
    this.agency = JSON.parse(localStorage.getItem('selectedAgency'));
    // Recuperar cliente seleccionado previamente del localStorage
    this.selectedClientEmail = this.clientEmail ? this.clientEmail : null;
    this.loadClients();
    this.loadCountries();
  }

  loadCountries(): void {
    this.itineraryService._restService.doGet('app', 'utilities/countries')
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe({
        next: (res: any) => {
          this.countriesData = res?.Countrys || [];
        },
        error: (err) => {
          console.error('Error loading countries:', err);
        }
      });
  }

  selectClient(client: any): void {
    // Mantener el estado visual del checkbox y en localStorage
    this.selectedClientEmail = client.email;

    // Resolver el país para el phone_code del cliente
    if (client.phone_code) {
      const cleanPhoneCode = client.phone_code.match(/\((\+\d+)\)/)
        ? client.phone_code.match(/\((\+\d+)\)/)[1].replace('+', '')
        : String(client.phone_code);

      const country = this.countriesData.find(c => String(c.phone_code) === cleanPhoneCode);
      if (country) {
        client.phone_code_resolved = `${country.country_name} (+${country.phone_code})`;
        client.phone_code_number = Number(country.phone_code);
      }
    }

    this.clientSelected.emit(client);
    this.close();
  }

  loadClients(page: number = 1): void {
    const filters = this.appliedFilters;
    const goPage = page > 1 ? `&page=${page}` : '';
    const firstName = filters.firstName ? `&first_name=${encodeURIComponent(filters.firstName)}` : '';
    const lastName = filters.lastName ? `&last_name=${encodeURIComponent(filters.lastName)}` : '';
    const email = filters.email ? `&email=${encodeURIComponent(filters.email)}` : '';

    // Si hay busqueda de texto libre, agregarla como filtro
    const search = this.searchText ? `&search=${encodeURIComponent(this.searchText)}` : '';

    const url = `clients?agency_id=${this.agency.agency_id}${goPage}${firstName}${lastName}${email}${search}&paginate=4&adult=true`;

    this.loading = true;
    this.itineraryService._restService.doGet('app', url)
      .pipe(
        takeUntil(this._unsubscribeAll),
        finalize(() => this.loading = false)
      )
      .subscribe({
        next: (response: any) => {
          if (response && response.data && response.data.data) {
            this.clients = response.data.data;
            this.currentPage = response.data.current_page || 1;
            this.lastPage = response.data.last_page || 1;
            this.totalItems = response.data.total || 0;
          } else if (response && response.data) {
            this.clients = response.data;
            this.totalItems = this.clients.length;
            this.lastPage = 1;
          } else {
            this.clients = [];
            this.totalItems = 0;
            this.lastPage = 1;
          }
        },
        error: (err) => {
          console.error('Error loading clients:', err);
          this.clients = [];
        }
      });
  }

  onFilterChange(): void {
    if (this.filterForm.invalid) {
      this.filterForm.markAllAsTouched();
      return;
    }

    const { firstName, lastName, email } = this.filterForm.value;
    this.appliedFilters = {
      firstName: (firstName || '').trim(),
      lastName: (lastName || '').trim(),
      email: (email || '').trim(),
    };

    // Reflejar en los inputs los valores sin espacios sobrantes
    this.filterForm.patchValue(this.appliedFilters, { emitEvent: false });

    this.currentPage = 1;
    this.loadClients();
  }

  /**
   * Indica si un campo debe mostrar error (solo luego de que el usuario lo tocó o editó)
   */
  hasError(controlName: string, error?: string): boolean {
    const control = this.filterForm.get(controlName);
    if (!control || !(control.touched || control.dirty)) {
      return false;
    }
    return error ? control.hasError(error) : control.invalid;
  }

  onSearchChange(): void {
    // La busqueda se dispara automaticamente con ngModelChange
    // esperamos un poco para que el usuario termine de escribir
    this.currentPage = 1;
    this.loadClients();
  }

  clearFilters(): void {
    this.filterForm.reset({ ...EMPTY_FILTERS });
    this.appliedFilters = { ...EMPTY_FILTERS };
    this.searchText = '';
    this.currentPage = 1;
    this.loadClients();
  }

  // Paginación
  get startItem(): number {
    if (this.totalItems === 0) return 0;
    return (this.currentPage - 1) * this.itemsPerPage + 1;
  }

  get endItem(): number {
    return Math.min(this.currentPage * this.itemsPerPage, this.totalItems);
  }

  get totalPages(): number {
    return Math.ceil(this.totalItems / this.itemsPerPage);
  }

  previousPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.loadClients(this.currentPage);
    }
  }

  nextPage(): void {
    if (this.currentPage < this.lastPage) {
      this.currentPage++;
      this.loadClients(this.currentPage);
    }
  }

  // Cerrar modal
  close(): void {
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
    this.bsModalRef.hide();
  }
}
